import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageSquare, X, Send, ArrowUpRight, Sparkles } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { scrollToId } from "../../lib/smoothScroll";

const API = process.env.REACT_APP_BACKEND_URL;
const sessionId = () => {
  let id = window.localStorage.getItem("margtor-chat-session");
  if (!id) {
    id = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem("margtor-chat-session", id);
  }
  return id;
};

export const AskMargtor = () => {
  const { lang, t } = useLang();
  const c = t.chat;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const goContact = () => {
    setOpen(false);
    if (pathname !== "/") navigate("/");
    setTimeout(() => scrollToId("contact"), pathname !== "/" ? 500 : 0);
  };

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: q, lang }),
      });
      if (!r.ok) throw new Error("chat failed");
      const d = await r.json();
      setMsgs((m) => [...m, { role: "assistant", content: d.reply, sources: d.sources, contact: d.suggest_contact }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: c.error, contact: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="chat-toggle"
        aria-label={c.title}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="chamfer-sm fixed bottom-5 left-5 z-[60] flex items-center gap-2 bg-ink px-4 py-3 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] shadow-[0_18px_40px_-16px_rgba(22,24,27,0.6)] transition-all duration-200 hover:bg-red-brand hover:-translate-y-0.5"
      >
        {open ? <X className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
        <span className="hidden sm:inline">{open ? c.close : c.launcher}</span>
      </button>

      {open && (
        <section
          data-testid="chat-panel"
          aria-label={c.title}
          className="fixed bottom-20 left-5 right-5 sm:right-auto sm:w-[400px] z-[60] flex max-h-[min(640px,calc(100vh-120px))] flex-col border border-[#f8f7f4]/15 bg-ink shadow-[0_30px_60px_-20px_rgba(22,24,27,0.7)]"
        >
          <header className="flex items-start justify-between gap-3 border-b border-[#f8f7f4]/10 px-5 py-4">
            <div>
              <p className="font-mono-tech text-[0.6rem] tracking-[0.28em] uppercase text-[#e63946]">{c.eyebrow}</p>
              <h3 className="mt-1 font-display text-lg font-bold tracking-tight text-[#f8f7f4]">{c.title}</h3>
            </div>
            <Sparkles className="h-4 w-4 shrink-0 text-[#e63946]" />
          </header>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4" data-testid="chat-messages">
            {msgs.length === 0 && (
              <div>
                <p className="text-sm leading-relaxed text-[#f8f7f4]/70">{c.welcome}</p>
                <div className="mt-4 flex flex-col gap-2">
                  {c.suggestions.map((s, i) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`chat-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="border border-[#f8f7f4]/15 bg-[#f8f7f4]/[0.04] px-3 py-2 text-left text-xs text-[#f8f7f4]/80 transition-colors hover:border-[#e63946]/60"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}`} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className={`max-w-[88%] px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-red-brand text-[#f8f7f4]" : "bg-[#f8f7f4]/[0.06] text-[#f8f7f4]/90 border border-[#f8f7f4]/10"}`}>
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  {m.sources?.length > 0 && (
                    <ul className="mt-2 space-y-1 border-t border-[#f8f7f4]/10 pt-2">
                      {m.sources.map((s) => (
                        <li key={s.url}>
                          <a href={s.url.replace("https://margtor.africa", "")} className="inline-flex items-center gap-1 font-mono-tech text-[10px] uppercase tracking-[0.12em] text-[#e63946] hover:text-[#f8f7f4]">
                            {s.title.slice(0, 60)} <ArrowUpRight className="h-3 w-3" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                  {m.contact && (
                    <button type="button" data-testid="chat-contact-cta" onClick={goContact} className="chamfer-sm mt-3 inline-flex items-center gap-2 bg-[#f8f7f4] px-3 py-2 font-mono-tech text-[10px] font-semibold uppercase tracking-[0.15em] text-ink hover:bg-[#e63946] hover:text-[#f8f7f4] transition-colors">
                      {c.contactCta} <ArrowUpRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && <p data-testid="chat-typing" className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#f8f7f4]/40">{c.typing}</p>}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-[#f8f7f4]/10 p-3"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={c.placeholder}
              maxLength={1500}
              className="flex-1 bg-[#f8f7f4]/[0.06] px-3 py-2.5 text-sm text-[#f8f7f4] placeholder:text-[#f8f7f4]/35 outline-none focus:ring-1 focus:ring-[#e63946]"
            />
            <button type="submit" data-testid="chat-send" disabled={busy || !input.trim()} aria-label={c.send} className="flex h-10 w-10 items-center justify-center bg-red-brand text-[#f8f7f4] disabled:opacity-40">
              <Send className="h-4 w-4" />
            </button>
          </form>
          <p className="px-5 pb-3 font-mono-tech text-[9px] uppercase tracking-[0.15em] text-[#f8f7f4]/35">{c.disclaimer}</p>
        </section>
      )}
    </>
  );
};
